import { Phone, UserIcon } from "@/components/icons";
import {
  Button,
  Input,
  ToggleGroup,
  ToggleGroupItem,
  toast,
} from "@/components/kit";
import { Modal } from "@/components/modal";
import {
  FieldContent,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import {
  customerAddressFormToPayload,
  needsManualAddressSplit,
  toCustomerAddressFormValue,
  type CustomerAddressFormValue,
} from "@/modules/customers/domain/address";
import { CustomerAddressFields } from "@/modules/customers/presentation/ui/CustomerAddressFields";
import { ShippingBranchField } from "@/modules/orders/presentation/ui/ShippingBranchField";
import type { OrderDetailDTO } from "@/modules/orders/domain/types";
import type { DeliveryMethod } from "@/modules/orders/domain/types";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useEffect, useState } from "react";
import { useUpdateOrderDetails } from "../api/queries";
import {
  DELIVERY_METHOD_META,
  SHIPPING_CARRIERS,
} from "./order-status";

type OrderDetailsEditDialogProps = {
  order: OrderDetailDTO;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function OrderDetailsEditDialog({
  order,
  open,
  onOpenChange,
}: OrderDetailsEditDialogProps) {
  const updateDetails = useUpdateOrderDetails();
  const [name, setName] = useState(order.customer.name);
  const [phone, setPhone] = useState(order.customer.phone ?? "");
  const [addressForm, setAddressForm] = useState<CustomerAddressFormValue>(
    () => toCustomerAddressFormValue(order.customer),
  );
  const [deliveryMethod, setDeliveryMethod] = useState(order.deliveryMethod);
  const [carrier, setCarrier] = useState(order.shippingCarrier ?? "");
  const [shippingBranch, setShippingBranch] = useState(
    order.shippingBranch ?? "",
  );

  useEffect(() => {
    if (!open) return;
    setName(order.customer.name);
    setPhone(order.customer.phone ?? "");
    setAddressForm(toCustomerAddressFormValue(order.customer));
    setDeliveryMethod(order.deliveryMethod);
    setCarrier(order.shippingCarrier ?? "");
    setShippingBranch(order.shippingBranch ?? "");
  }, [open, order]);

  function handleDeliveryMethodChange(method: DeliveryMethod) {
    setDeliveryMethod(method);
    if (method !== "carrier") {
      setCarrier("");
      setShippingBranch("");
    }
  }

  function handleSave() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("ກະລຸນາໃສ່ຊື່ລູກຄ້າ");
      return;
    }

    updateDetails.mutate(
      {
        id: order.id,
        input: {
          customer: {
            name: trimmedName,
            phone: phone.trim() || null,
            ...customerAddressFormToPayload(addressForm),
          },
          deliveryMethod,
          shippingCarrier:
            deliveryMethod === "carrier" ? carrier.trim() || null : null,
          shippingBranch:
            deliveryMethod === "carrier" ? shippingBranch.trim() || null : null,
        },
      },
      {
        onSuccess: () => {
          toast.success("ບັນທຶກຂໍ້ມູນແລ້ວ");
          onOpenChange(false);
        },
        onError: (e) =>
          toast.error("ບັນທຶກບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );
  }

  const showLegacyHint = needsManualAddressSplit(order.customer);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ແກ້ໄຂຂໍ້ມູນລູກຄ້າ & ຂົນສົ່ງ"
      description="ແກ້ຊື່ ເບີໂທ ທີ່ຢູ່ ແລະ ວິທີຂົນສົ່ງ"
      size="lg"
      footer={
        <>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={updateDetails.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            className="rounded-full"
            isLoading={updateDetails.isPending}
            onClick={handleSave}
          >
            ບັນທຶກ
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FieldLayout>
          <FieldLabel htmlFor="edit-cust-name">
            <UserIcon className="size-4" /> ຊື່ລູກຄ້າ
          </FieldLabel>
          <FieldContent>
            <Input
              id="edit-cust-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FieldContent>
        </FieldLayout>

        <FieldLayout>
          <FieldLabel htmlFor="edit-cust-phone">
            <Phone className="size-4" /> ເບີໂທ
          </FieldLabel>
          <FieldContent>
            <Input
              id="edit-cust-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </FieldContent>
        </FieldLayout>

        <CustomerAddressFields
          value={addressForm}
          onChange={setAddressForm}
          idPrefix="edit-cust"
          legacyAddressHint={showLegacyHint ? order.customer.address : null}
        />

        <FieldLayout>
          <FieldLabel>ວິທີຮັບສິນຄ້າ</FieldLabel>
          <FieldContent>
            <ToggleGroup
              type="single"
              variant="outline"
              value={deliveryMethod}
              onValueChange={(value) => {
                if (
                  value === "carrier" ||
                  value === "pickup" ||
                  value === "self_delivery"
                ) {
                  handleDeliveryMethodChange(value);
                }
              }}
              className="grid w-full grid-cols-3 gap-2"
            >
              {(Object.keys(DELIVERY_METHOD_META) as DeliveryMethod[]).map(
                (key) => (
                  <ToggleGroupItem
                    key={key}
                    value={key}
                    className="min-h-11 rounded-full px-2 text-xs data-[state=on]:border-secondary data-[state=on]:bg-secondary/15 data-[state=on]:text-foreground sm:text-sm"
                  >
                    {DELIVERY_METHOD_META[key].shortLabel}
                  </ToggleGroupItem>
                ),
              )}
            </ToggleGroup>
          </FieldContent>
        </FieldLayout>

        {deliveryMethod === "carrier" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldLayout>
              <FieldLabel>ບໍລິສັດຂົນສົ່ງ</FieldLabel>
              <FieldContent>
                <SimpleSelect
                  value={carrier || SHIPPING_CARRIERS[0]}
                  onValueChange={(value) => {
                    setCarrier(value);
                    setShippingBranch("");
                  }}
                  options={SHIPPING_CARRIERS.map((c) => ({
                    value: c,
                    label: c,
                  }))}
                />
              </FieldContent>
            </FieldLayout>
            <ShippingBranchField
              id="edit-shipping-branch"
              value={shippingBranch}
              onChange={setShippingBranch}
              carrier={carrier}
              address={addressForm}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
